import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import {
  Calendar, IdCard, Loader2, Mail, MapPin, Phone, Save,
  ShieldCheck, Target, User,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import client from '../../lib/api';
import ProgressFill from '../../components/ProgressFill';
import { accountHook } from '../../lib/domHooks';

const Profile = () => {
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  // Ödül kartları bir süre burada `#bonuslarim` çapasıyla duruyordu; artık
  // kendi sayfaları var (pages/account/MyBonuses.jsx). Paylaşılmış ya da
  // tarayıcıda kalmış eski adres oraya yönlenir.
  const { hash } = useLocation();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: '', phone: '', first_name: '', last_name: '',
    birth_date: '', city: '', tc_no: '',
  });
  const wagerRequired = Number(user?.wager_required || 0);
  const wagerCompleted = Number(user?.wager_completed || 0);
  const wagerRemaining = Number(user?.wager_remaining || 0);
  // Çevrim kartı yalnızca aktif bir çevrim varken çizilir: boş kart
  // ("0,00 ₺ · Aktif çevrim şartınız bulunmuyor") profilin başında yer
  // kaplıyor ve oyuncuya hiçbir şey söylemiyordu. Kural bir süre tek bir
  // şemaya bağlıydı; boş kartın gereksizliği şemadan bağımsızdır.
  const showWager = wagerRemaining > 0;
  const wagerProgress = wagerRequired > 0
    ? Math.min(100, (wagerCompleted / wagerRequired) * 100)
    : 100;
  const formatWager = (value) => (
    `${Number(value || 0).toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ₺`
  );

  useEffect(() => {
    if (!user) return;
    setForm({
      email: user.email || '',
      phone: user.phone || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      birth_date: user.birth_date || '',
      city: user.city || '',
      tc_no: user.tc_no || '',
    });
  }, [user]);

  // Kayıtlı TC ve doğum tarihini üye değiştiremez (sunucu da 409 döner);
  // düzeltme yalnızca panelden yapılır.
  const tcLocked = Boolean(user?.tc_no);
  const birthLocked = Boolean(user?.birth_date);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await client.patch('/auth/me', form);
      await refresh();
      toast({ title: 'Profil güncellendi', description: 'Bilgileriniz kaydedildi' });
    } catch (error) {
      toast({ title: 'Kaydedilemedi', description: error.response?.data?.detail || 'Bir hata oluştu' });
    } finally {
      setSaving(false);
    }
  };

  if (hash === '#bonuslarim') return <Navigate to="/hesap/bonuslarim" replace />;

  return (
    <div className="pusu-panel p-6" {...accountHook('panel')}>
        <div className="mb-5" {...accountHook('page-header')}>
          <div>
            <h2 className="text-xl font-bold gold-text" {...accountHook('title')}>Profil Bilgilerim</h2>
            <p className="text-sm text-gray-400" {...accountHook('subtitle')}>Kişisel bilgilerinizi güncelleyebilirsiniz</p>
          </div>
        </div>
        <div className="pusu-card p-4 mb-5 flex flex-wrap items-center gap-3" {...accountHook('security-callout')}>
          <ShieldCheck size={22} className="shrink-0 text-[var(--pusu-gold)]" />
          <div className="flex-1 min-w-[180px]">
            <div className="font-semibold">Google Authenticator ile hesap güvenliği</div>
            <p className="account-muted text-xs mt-1">
              İsteğe bağlı iki adımlı doğrulamayı QR kodu veya kurulum anahtarıyla açabilirsiniz.
            </p>
          </div>
          <Link to="/hesap/guvenlik" className="gold-outline-btn px-4 py-2 rounded-xl text-sm">
            Güvenlik Ayarları
          </Link>
        </div>
        {showWager && (
        <div className="pusu-card p-4 mb-5" {...accountHook('wager')}>
          <div className="flex items-start gap-3">
            <div className="account-icon-amber w-10 h-10 shrink-0 rounded-xl flex items-center justify-center">
              <Target size={20} className="text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500">
                    Kalan Çevrim Şartı
                  </div>
                  <div className="mt-1 text-xl font-black text-amber-400">
                    {formatWager(wagerRemaining)}
                  </div>
                </div>
                <div className="text-right text-xs text-gray-400">
                  <div>{formatWager(wagerCompleted)} tamamlandı</div>
                  <div>{formatWager(wagerRequired)} toplam</div>
                </div>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10" {...accountHook('progress')}>
                <ProgressFill percent={wagerProgress} className="h-full rounded-full bg-amber-400 transition-all" />
              </div>
              {wagerRemaining <= 0 && (
                <div className="mt-2 text-xs text-green-400">
                  Aktif çevrim şartınız bulunmuyor.
                </div>
              )}
            </div>
          </div>
        </div>
        )}
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4" {...accountHook('form')}>
          <Field name="username" icon={User} label="Kullanıcı Adı"><input disabled className="pusu-input pl-10 opacity-60" value={user?.username || ''} readOnly /></Field>
          <Field name="email" icon={Mail} label="E-Posta"><input className="pusu-input pl-10" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></Field>
          <Field name="first_name" icon={User} label="Ad"><input className="pusu-input pl-10" value={form.first_name} onChange={(event) => setForm({ ...form, first_name: event.target.value })} /></Field>
          <Field name="last_name" icon={User} label="Soyad"><input className="pusu-input pl-10" value={form.last_name} onChange={(event) => setForm({ ...form, last_name: event.target.value })} /></Field>
          <Field name="phone" icon={Phone} label="Telefon"><input className="pusu-input pl-10" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></Field>
          <Field name="tc_no" icon={IdCard} label="T.C. Kimlik No"><input readOnly={tcLocked} title={tcLocked ? 'Kayıtlı TC kimlik numarası değiştirilemez' : undefined} className={`pusu-input pl-10${tcLocked ? ' opacity-60' : ''}`} value={form.tc_no} onChange={(event) => setForm({ ...form, tc_no: event.target.value })} /></Field>
          <Field name="birth_date" icon={Calendar} label="Doğum Tarihi"><input type="date" readOnly={birthLocked} title={birthLocked ? 'Kayıtlı doğum tarihi değiştirilemez' : undefined} className={`pusu-input pl-10${birthLocked ? ' opacity-60' : ''}`} value={form.birth_date} onChange={(event) => setForm({ ...form, birth_date: event.target.value })} /></Field>
          <Field name="city" icon={MapPin} label="Şehir"><input className="pusu-input pl-10" value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} /></Field>
          {(tcLocked || birthLocked) && (
            <p className="md:col-span-2 text-xs text-gray-400">
              T.C. kimlik numarası ve doğum tarihi kaydedildikten sonra değiştirilemez.
              Hatalıysa canlı destekle iletişime geçin.
            </p>
          )}
          <div className="md:col-span-2 flex justify-end pt-2" {...accountHook('actions')}>
            <button disabled={saving} type="submit" {...accountHook('submit')} className="gold-btn px-6 py-3 rounded-xl flex items-center gap-2 disabled:opacity-50">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Bilgileri Kaydet
            </button>
          </div>
        </form>
    </div>
  );
};

const Field = ({
  name, icon: Icon, label, children,
}) => (
  <div {...accountHook('field', name)}>
    <label className="text-xs text-gray-400 mb-1.5 block">{label}</label>
    <div className="relative">
      <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]" />
      {children}
    </div>
  </div>
);

export default Profile;
